import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateStorageLocationDto,
  MonitoringMethod,
  StorageLocationDto,
} from '@services/dtos'
import { storageLocationSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid } from '@mui/material'
import {
  createStorageLocation,
  updateStorageLocation,
} from '@state/reducers/storageLocationReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { MonitoringMethodLabels, StorageTypeLabels } from '@utils/constants'
import { FormCrudHeader } from './FormCrudHeader'
import { findAllRooms } from '@state/thunks/roomThunk'
import { useSelector } from '@state/store'
import { RoomType } from '@services/api'

type Props = {
  editingStorageLocation?: StorageLocationDto | null
  onClose: () => void
}

const StorageLocationForm: React.FC<Props> = ({
  editingStorageLocation,
  onClose,
}) => {
  const { watch, handleSubmit, reset, control } =
    useForm<CreateStorageLocationDto>({
      defaultValues: editingStorageLocation
        ? {
            name: editingStorageLocation.name,
            type: editingStorageLocation.type,
            capacityL: editingStorageLocation.capacityL,
            setpointC: editingStorageLocation.setpointC,
            rangeMinC: editingStorageLocation.rangeMinC,
            rangeMaxC: editingStorageLocation.rangeMaxC,
            alarmLowC: editingStorageLocation.alarmLowC,
            alarmHighC: editingStorageLocation.alarmHighC,
            monitoring: editingStorageLocation.monitoring,
            roomId: editingStorageLocation.room.id,
            notes: editingStorageLocation.notes,
          }
        : { monitoring: MonitoringMethod.MANUAL },
      resolver: yupResolver(storageLocationSchema),
    })

  const dispatch = useAppDispatch()
  const { rooms } = useSelector(({ room }) => ({
    rooms: room.rooms.filter((_room) => _room.type === RoomType.LabWorkstation),
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    dispatch(findAllRooms())
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateStorageLocationDto) => {
    setIsSubmitting(true)

    if (editingStorageLocation) {
      dispatch(
        updateStorageLocation({ id: editingStorageLocation.id, dto: data }),
      )
        .unwrap()
        .then(() => {
          cb('Le stockage a été modifié avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le nom n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createStorageLocation(data))
        .unwrap()
        .then(() => {
          cb('Le stockage a été créé avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le nom n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields = [
    { name: 'capacityL', label: 'Capacité (L)', type: 'number' },
    { name: 'name', label: 'Nom', sm: 12 },
    { name: 'setpointC', label: 'Consigne (°C)', type: 'number', sm: 4 },
    { name: 'rangeMinC', label: 'Min (°C)', type: 'number', sm: 4 },
    { name: 'rangeMaxC', label: 'Max (°C)', type: 'number', sm: 4 },
    { name: 'alarmLowC', label: 'Alarme basse (°C)', type: 'number' },
    { name: 'alarmHighC', label: 'Alarme haute (°C)', type: 'number' },
    { name: 'notes', label: 'Notes', type: 'textarea', sm: 12 },
  ]
  const selects: {
    name: 'type' | 'monitoring' | 'roomId'
    label: string
    ref: Record<string, string>
  }[] = [
    { name: 'type', label: 'Type', ref: StorageTypeLabels },
    { name: 'monitoring', label: 'Monitoring', ref: MonitoringMethodLabels },
    {
      name: 'roomId',
      label: 'Salle',
      ref: rooms.reduce((acc, room) => {
        acc[room.id] = room.label
        return acc
      }, {}),
    },
  ]

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingStorageLocation}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Stockage
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {selects.map(({ name, label, ref }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <SelectField
                    key={name}
                    control={control}
                    name={name}
                    options={Object.entries(ref).map(([value, label]) => ({
                      label,
                      value,
                    }))}
                    value={watch(name)}
                    label={label}
                  />
                </Grid>
              ))}
              {fields.map((field) => (
                <Grid
                  item
                  xs={12}
                  sm={field.sm ? field.sm : 6}
                  key={field.name}
                >
                  <InputField
                    control={control}
                    name={field.name}
                    label={field.label}
                    type={field.type ? field.type : 'text'}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 2 : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default StorageLocationForm
